import { useEffect, useRef } from "react";
import { IBlock } from "../BlockList/Block";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { filterBlocksBySearch } from "./blockSearchFilter";

function blocksNeedUpdate(
	blocks: IBlock[],
	previousBlocks: IBlock[],
): { update: boolean; debug: { blocksChanged: boolean; badgesCountChanged: boolean; hasNewBadges: boolean; shouldForceUpdateForBadges: boolean } } {
	const blocksChanged =
		blocks.length !== previousBlocks.length ||
		blocks.some((block, index) => {
			const existing = previousBlocks[index];
			if (!existing) return true;
			return (
				block.id !== existing.id ||
				block.statusBadge !== existing.statusBadge ||
				block.officeColor !== existing.officeColor ||
				block.isActive !== existing.isActive ||
				block.primaryText !== existing.primaryText ||
				block.secondaryText !== existing.secondaryText
			);
		});
	const previousBadgesCount = previousBlocks.filter((b) => b.statusBadge).length;
	const currentBadgesCount = blocks.filter((b) => b.statusBadge).length;
	const badgesCountChanged = previousBadgesCount !== currentBadgesCount;
	const hasNewBadges = blocks.some((block, index) => {
		const existing = previousBlocks[index];
		return block.statusBadge && (!existing || existing.statusBadge !== block.statusBadge);
	});
	const shouldForceUpdateForBadges = currentBadgesCount > 0 && previousBadgesCount === 0 && blocks.length > 0;
	const update =
		blocksChanged ||
		blocks.length === 0 ||
		badgesCountChanged ||
		hasNewBadges ||
		shouldForceUpdateForBadges;
	return {
		update,
		debug: { blocksChanged, badgesCountChanged, hasNewBadges, shouldForceUpdateForBadges },
	};
}

/**
 * Hook to synchronize blocks with deep comparison to handle badge updates.
 *
 * When new batches arrive (progressive folder list load), the visible list is
 * re-derived by re-applying the active search filter (`searchValueRef`) instead
 * of resetting to the full unfiltered set. This keeps the search results stable
 * while batches load.
 */
export function useBlocksSync(
	blocks: IBlock[],
	setBlocksShowing: (blocks: IBlock[]) => void,
	searchValueRef: React.MutableRefObject<string>,
): void {
	const previousBlocksRef = useRef<IBlock[]>([]);

	useEffect(() => {
		const previousBlocks = previousBlocksRef.current;
		const { update, debug } = blocksNeedUpdate(blocks, previousBlocks);
		const referenceChanged = blocks !== previousBlocksRef.current;

		if (update || referenceChanged) {
			LoggerService.debug("🔍 [SearchBlockList] Blocks updated", {
				blocksCount: blocks.length,
				blocksWithBadges: blocks.filter((b) => b.statusBadge).length,
				...debug,
				previousCount: previousBlocks.length,
				referenceChanged,
				blocksSample: blocks.slice(0, 2).map((b) => ({ id: b.id, statusBadge: b.statusBadge })),
				activeSearch: searchValueRef.current ? "present" : "empty",
			});
			setBlocksShowing(filterBlocksBySearch(blocks, searchValueRef.current));
			previousBlocksRef.current = blocks;
		} else {
			LoggerService.debug("🔍 [SearchBlockList] Blocks NOT updated (no changes detected)", {
				blocksCount: blocks.length,
				blocksWithBadges: blocks.filter((b) => b.statusBadge).length,
				...debug,
			});
		}
	}, [blocks, setBlocksShowing, searchValueRef]);
}
